import { Link } from '@inertiajs/react'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle, Ship, Calendar, Activity, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import { useOrgPath } from '@/lib/pathFor'
import { formatDate, formatShortDateTime, formatBookingRef } from '@/lib/formatters'

interface Attention {
  escalated: number
  agent_failed: number
  send_failed: number
  draft_awaiting_review: number
  ingestion_failed: number
  unassigned: number
  unknown_sender: number
}

interface TopRow {
  name: string
  bookings: number
  containers: number
}

interface ActiveBookings {
  total: number
  with_pending_si: number
  with_pending_bl: number
}

interface UpcomingSailing {
  id: string
  shipment_internal_reference: string | null
  carrier_booking_id: string
  client_email: string | null
  sailing_date: string
  incomplete_activities: string[]
  show_url: string
}

interface ActivityEvent {
  type: string
  description: string | null
  date: string
  reference: string | null
  url: string
}

interface DashboardIndexProps {
  attention: Attention
  pending_threads: number
  active_bookings: ActiveBookings
  top_clients: TopRow[]
  top_destinations: TopRow[]
  top_carriers: TopRow[]
  upcoming_sailings: UpcomingSailing[]
  recent_activity: ActivityEvent[]
}

const SAILINGS_PAGE_SIZE = 10
const ACTIVITY_PAGE_SIZE = 7

const ATTENTION_LABELS: { key: keyof Attention; label: string }[] = [
  { key: 'escalated', label: 'Escalated' },
  { key: 'agent_failed', label: 'Agent failed' },
  { key: 'send_failed', label: 'Send failed' },
  { key: 'draft_awaiting_review', label: 'Draft awaiting review' },
  { key: 'ingestion_failed', label: 'Ingestion failed' },
  { key: 'unassigned', label: 'Unassigned thread' },
  { key: 'unknown_sender', label: 'Unknown sender' },
]

function TopTable({ title, rows }: { title: string; rows: TopRow[] }) {
  return (
    <section className="min-w-0">
      <h2 className="text-[13px] font-semibold text-foreground">{title}</h2>
      <p className="text-[10px] text-muted-foreground mb-3">Sailing −90 to +30 days · confirmed+ bookings</p>
      {rows.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Bookings</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Containers</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name} className="border-b last:border-0">
                  <td className="px-4 py-2 text-[13px] font-medium truncate">{row.name}</td>
                  <td className="px-4 py-2 text-[13px] text-right text-muted-foreground">{row.bookings}</td>
                  <td className="px-4 py-2 text-[13px] text-right text-muted-foreground">{row.containers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">No bookings yet</p>
      )}
    </section>
  )
}

export default function DashboardIndex({
  attention,
  pending_threads,
  active_bookings,
  top_clients,
  top_destinations,
  top_carriers,
  upcoming_sailings,
  recent_activity,
}: DashboardIndexProps) {
  const pathFor = useOrgPath()
  const [sailingsPage, setSailingsPage] = useState(0)
  const [activityPage, setActivityPage] = useState(0)
  const [lookupSearch, setLookupSearch] = useState('')

  const sailingsTotal = Math.ceil(upcoming_sailings.length / SAILINGS_PAGE_SIZE)
  const activityTotal = Math.ceil(recent_activity.length / ACTIVITY_PAGE_SIZE)

  const visibleSailings = upcoming_sailings.slice(sailingsPage * SAILINGS_PAGE_SIZE, (sailingsPage + 1) * SAILINGS_PAGE_SIZE)
  const visibleActivity = recent_activity.slice(activityPage * ACTIVITY_PAGE_SIZE, (activityPage + 1) * ACTIVITY_PAGE_SIZE)
  const attentionTiles = ATTENTION_LABELS.filter(({ key }) => attention[key] > 0)

  function handleLookupSubmit(e: React.FormEvent) {
    e.preventDefault()
    router.get(pathFor('/shipments'), { search: lookupSearch }, { preserveState: false })
  }

  return (
    <>
      <header className="flex h-14 items-center border-b px-8">
        <h1 className="text-sm font-semibold">Dashboard</h1>
      </header>

      <div className="flex-1 overflow-y-auto p-8 space-y-8">
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-3">Shipment Lookup</h2>
          <form onSubmit={handleLookupSubmit} className="flex max-w-2xl items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={lookupSearch}
                onChange={(event) => setLookupSearch(event.target.value)}
                placeholder="Paste Internal Ref, carrier booking ID, client, route, or document number"
                className="h-10 pl-9 text-[13px]"
              />
            </div>
            <Button type="submit" className="h-10 cursor-pointer">
              Search
            </Button>
          </form>
        </section>

        {/* Needs attention */}
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-3">Needs attention</h2>
          {attentionTiles.length > 0 ? (
            <div className="grid grid-cols-3 gap-4">
              {attentionTiles.map(({ key, label }) => (
                <Link key={key} href={pathFor(`/inbox?filter=open&attention=${key}`)} className="rounded-lg border p-4 hover:bg-muted/30 space-y-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-2xl font-bold text-foreground">{attention[key]}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">Nothing needs attention</p>
          )}
        </section>

        {/* KPIs */}
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-3">Overview</h2>
          <div className="grid grid-cols-4 gap-4">
            <Link href={pathFor('/shipments')} className="rounded-lg border p-4 hover:bg-muted/30 space-y-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Ship className="h-4 w-4 text-primary" />
                <span className="text-2xl font-bold text-foreground">{active_bookings.total}</span>
              </div>
              <p className="text-xs text-muted-foreground">Active bookings</p>
            </Link>
            <Link href={pathFor('/inbox?filter=pending')} className="rounded-lg border p-4 hover:bg-muted/30 space-y-1 cursor-pointer">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                <span className="text-2xl font-bold text-foreground">{pending_threads}</span>
              </div>
              <p className="text-xs text-muted-foreground">Pending threads</p>
            </Link>
            <Link href={pathFor('/shipments')} className="rounded-lg border p-4 hover:bg-muted/30 space-y-1 cursor-pointer">
              <span className="text-2xl font-bold text-foreground">{active_bookings.with_pending_si}</span>
              <p className="text-xs text-muted-foreground">SI incomplete</p>
            </Link>
            <Link href={pathFor('/shipments')} className="rounded-lg border p-4 hover:bg-muted/30 space-y-1 cursor-pointer">
              <span className="text-2xl font-bold text-foreground">{active_bookings.with_pending_bl}</span>
              <p className="text-xs text-muted-foreground">BL incomplete</p>
            </Link>
          </div>
        </section>

        {/* Top lists */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <TopTable title="Top clients" rows={top_clients} />
          <TopTable title="Top destinations" rows={top_destinations} />
          <TopTable title="Top carriers" rows={top_carriers} />
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Upcoming Sailings */}
          <section className="min-w-0">
            <h2 className="text-[13px] font-semibold text-foreground mb-3">Upcoming Sailings (7 days)</h2>
            {upcoming_sailings.length > 0 ? (
              <>
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Internal Ref</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Carrier booking ID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Client</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Sailing</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Incomplete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSailings.map((s) => (
                        <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => router.visit(s.show_url)}>
                          <td className="px-4 py-2 text-[13px] font-mono font-medium">{s.shipment_internal_reference || 'No Internal Ref'}</td>
                          <td className="px-4 py-2 text-[13px] text-muted-foreground">{formatBookingRef(s.carrier_booking_id)}</td>
                          <td className="px-4 py-2 text-[13px] text-muted-foreground">{s.client_email || '-'}</td>
                          <td className="px-4 py-2 text-[13px] text-muted-foreground">{formatDate(s.sailing_date)}</td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1">
                              {s.incomplete_activities.map((act) => (
                                <Badge key={act} variant="secondary" size="xs" className="font-medium bg-amber-100 text-amber-800">
                                  {act}
                                </Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-end gap-3 mt-2">
                  {sailingsTotal > 1 && <span className="text-[10px] text-muted-foreground">{sailingsPage + 1} / {sailingsTotal}</span>}
                  <button
                    onClick={() => setSailingsPage(sailingsPage - 1)}
                    disabled={sailingsPage === 0}
                    className={`text-xs font-medium ${sailingsPage > 0 ? 'text-foreground hover:underline cursor-pointer' : 'text-muted-foreground/50'}`}
                  >
                    ‹ Previous
                  </button>
                  <button
                    onClick={() => setSailingsPage(sailingsPage + 1)}
                    disabled={sailingsPage >= sailingsTotal - 1}
                    className={`text-xs font-medium ${sailingsPage < sailingsTotal - 1 ? 'text-foreground hover:underline cursor-pointer' : 'text-muted-foreground/50'}`}
                  >
                    Next ›
                  </button>
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground italic">No upcoming sailings</p>
            )}
          </section>

          {/* Recent Activity */}
          <section className="min-w-0">
            <h2 className="text-[13px] font-semibold text-foreground mb-3">Recent Activity</h2>
            {recent_activity.length > 0 ? (
              <>
                <div className="space-y-0 rounded-lg border divide-y">
                  {visibleActivity.map((event, i) => (
                    <Link key={i} href={event.url} className="flex items-start gap-3 px-4 py-2.5 hover:bg-muted/30 cursor-pointer">
                      <Activity className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-foreground truncate">{event.description || 'No description'}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {event.reference && <span className="font-mono text-muted-foreground/60">{event.reference}</span>}
                          {event.reference && event.date && <span> · </span>}
                          {event.date ? formatShortDateTime(event.date) : ''}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-3 mt-2">
                  {activityTotal > 1 && <span className="text-[10px] text-muted-foreground">{activityPage + 1} / {activityTotal}</span>}
                  <button
                    onClick={() => setActivityPage(activityPage - 1)}
                    disabled={activityPage === 0}
                    className={`text-xs font-medium ${activityPage > 0 ? 'text-foreground hover:underline cursor-pointer' : 'text-muted-foreground/50'}`}
                  >
                    ‹ Previous
                  </button>
                  <button
                    onClick={() => setActivityPage(activityPage + 1)}
                    disabled={activityPage >= activityTotal - 1}
                    className={`text-xs font-medium ${activityPage < activityTotal - 1 ? 'text-foreground hover:underline cursor-pointer' : 'text-muted-foreground/50'}`}
                  >
                    Next ›
                  </button>
                </div>
              </>
            ) : (
              <p className="px-4 py-4 text-xs text-muted-foreground">No recent activity</p>
            )}
          </section>
        </div>
      </div>
    </>
  )
}

DashboardIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
